import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import usePeriodGoalsApi, {
  computeWeightBand, GOAL_WRITING_OPEN_STATUSES, EDITABLE_STATUSES, isDeletableGoalStatus,
} from './config';
import usePerformanceSettings from '../../hooks/usePerformanceSettings';
import GoalEditor from './GoalEditor';
import GenericSelect from '../../components/ui/GenericSelect';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Button from '../../components/ui/Button';
import CheckboxInput from '../../components/ui/CheckboxInput';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { parseTableParams, formatDate } from '../../utils/Utils';

const Container = styled.div``;

// Kanonik liste-sayfası anatomisi (bkz. EmployeesPage.js) — sayfa-üstü başlık
// YOK, başlık yalnız DataTable'ın title prop'unda. Dönem seçici, EmployeesPage'in
// StatusToggle'ı gibi tablonun ÜSTÜNDE serbest bir kontrol satırı olarak durur,
// bir başlıkla eşleştirilmez.
const PeriodSelectWrap = styled.div`
  min-width: 280px;
  margin-bottom: 16px;
`;

const HintRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;

const HintText = styled.span`
  font-size: 12px;
  color: #6c757d;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  overflow: hidden;
`;

const EmptyBox = styled.div`
  padding: 26px 18px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
`;

// Plan 3, Task 8 — kalıcı ağırlık toplamı bandı. Renk/rozet dili ObPeriodApprovalsPage'in
// Ok/Warn (#1A6E38/#A02525) ve config/index.js'teki STATUS_COLORS.closed/approved
// paletiyle tutarlı: geçerli (=100, eksiksiz) yeşil, eksik/hatalı sarımsı amber —
// weightSumInvalid'in ("kapı") sert kırmızısından bilinçli olarak ayrılır, çünkü bu
// bant dönem boyunca NORMAL bir ara-durum gösterir (henüz tüm hedefler girilmemiş
// olabilir), bir HATA değil.
const WeightBand = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 9px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 14px;
  background: ${(p) => (p.$valid ? '#DCFCE7' : '#FFFBEB')};
  color: ${(p) => (p.$valid ? '#16A34A' : '#D97706')};
  border: 1px solid ${(p) => (p.$valid ? '#BBF7D0' : '#FDE68A')};
`;

const MissingTag = styled.span`
  font-weight: 500;
  font-size: 12px;
  color: #B02A37;
`;

// Plan 3, Task 8 — approveClosure kapı-3 reddinin (performance.goal.weightSumInvalid)
// EK bağlamı: interceptor toast'u zaten anahtar metnini basıyor (services/api.js), bu
// yalnız bandın yanında currentSum'ı vurgulayan bir "flash" (bkz. config/index.js
// weightGateError yorumu — bir sonraki liste yüklemesinde temizlenir).
const GateFlash = styled.span`
  font-weight: 700;
  font-size: 12px;
  color: #B02A37;
`;

// #71 (UI düzeltme demeti, spec 2026-08-09, T4) — toplu onaya gönderme. Seçim
// çubuğu WeightBand'in HEMEN ÜSTÜNDE/altında değil, LİSTENİN üstünde ayrı bir
// satır (yalnız ≥1 satır seçiliyken render edilir — mui-datatables'ın kendi
// "N satır seçildi" araç çubuğunun AYNI görünürlük idiomu, ama bu sayfada
// DataTable.js'in yerleşik seçim mekanizması KULLANILMADI: o mekanizma
// yalnız onRowsDelete'e kablolanmış [bkz. DataTable.js selectableRows], "onaya
// gönder" için genellemek paylaşılan bileşene dokunmak demek olurdu — T2/T3
// ile AYNI karar [Button.js/DataTable.js bilerek DOKUNULMADI, sayfa-yerel
// checkbox kolonu tercih edildi]).
const BulkBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  margin-bottom: 14px;
  background: rgba(2, 145, 239, 0.06);
  border: 1px solid rgba(2, 145, 239, 0.18);
  border-radius: 6px;
`;

const BulkCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

// Sonuç paneli — BulkAssignModal'ın sonuç aşaması (ResultCreated/SkippedTitle/
// SkippedRow) ile AYNI renk dili (#1B7A3E başarı, #B02A37 satır-bazlı neden,
// #F1F5F9 ayraç) — komşu bileşen deseni, icat edilmedi.
const ResultBox = styled.div`
  padding: 14px 16px;
  margin-bottom: 14px;
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 8px;
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const ResultSummary = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: #1B7A3E;
  margin: 0;
`;

const DismissBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  color: #A0A8B4;
  padding: 4px;
  &:hover { color: #004242; }
`;

const FailedTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 10px 0 8px;
`;

const FailedRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 12px;
  color: #B02A37;
  padding: 6px 0;
  border-bottom: 1px solid #F1F5F9;
  &:last-child { border-bottom: none; }
`;

// customBodyRender hücresi — checkbox tıklaması satırın onRowClick'ini
// (GoalEditor'ı açan) TETİKLEMEMELİ, bkz. handler.
const BulkSelectCell = styled.div``;

// Task 26 fix (Important #3) — hedef listesi standart DataTable bileşenine taşındı;
// desen MyDevelopmentPlansPage'in self-servis DataTable kullanımından birebir alındı
// (queryParams state + parseTableParams + baseColumns'a sayfa-seviyeli aksiyon kolonu
// eklenmesi). Durum rozeti kolonu (STATUS_COLORS/StatusBadge) hook'un kendi
// customBodyRender'ında üretiliyor (bkz. config/index.js).
// PM Analitik Faz 2 (E18 "Performansım" > Hedeflerim sekmesi) — gömülü kullanım için isteğe bağlı prop'lar; rota olarak
// açıldığında hiçbiri verilmez ve davranış aynen eskisidir:
//   initialPeriodId  — çalışma alanında seçili dönem (dönem listesindeyse onunla açılır),
//   openGoalId       — Özet'teki hedef kartından gelinmişse o hedefin düzenleyicisi bir kez açılır,
//   tableBodyMaxHeight — sekme kabının tek kaydırması için DataTable gövde yüksekliği ('none' = iç kaydırma yok).
export default function MyGoalsPage({ initialPeriodId = null, openGoalId = null, tableBodyMaxHeight } = {}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [activeGoal, setActiveGoal] = useState(null);

  const {
    periods, periodsFetching, goals, goalsFetching, pagination, columns: baseColumns, refresh,
    getById, deleteGoal, createDraft, updateDraft, submitGoal, submitBulkGoals, updateKrProgress, updateKpiActual,
    submitFinalKpi, requestRevision, setParent, setAlignedOrgGoal, submitEmployeeScores, approveClosure,
    equalizeWeights, weightGateError,
  } = usePeriodGoalsApi(selectedPeriodId, queryParams);
  const { settings } = usePerformanceSettings();

  // Tek aktif/değerlendirme dönemi varsa otomatik seçili gelir.
  useEffect(() => {
    if (!selectedPeriodId && periods.length > 0) {
      const preferred = initialPeriodId && periods.some((p) => p.id === initialPeriodId) ? initialPeriodId : periods[0].id;
      setSelectedPeriodId(preferred);
    }
  }, [periods, selectedPeriodId, initialPeriodId]);

  const selectedPeriod = useMemo(
    () => periods.find((p) => p.id === selectedPeriodId) || null,
    [periods, selectedPeriodId],
  );

  // Plan 3, Task 8 — kalıcı ağırlık toplamı bandı. AĞIRLIK KÜMESİ ile aynı tanım
  // (closed hariç aktif hedefler), ancak brief'in istediği gibi FE'de DOĞRUDAN
  // `goals` listesinden türetilir (ayrı bir agregasyon ucu YOK — bkz.
  // computeWeightBand yorumu, task-8-report.md'deki sayfalama sınırlaması notu).
  const weightBand = useMemo(() => computeWeightBand(goals), [goals]);

  // "Ağırlıkları Eşitle" backend'de IsGoalWritingOpen (active|evaluation) kapısını
  // kullanır — canCreateGoal'ın (yalnız active, hedef OLUŞTURMA için) tersine burada
  // GOAL_WRITING_OPEN_STATUSES kullanılır (bkz. config/index.js yorumu). Hiç hedef
  // yoksa (boş AĞIRLIK KÜMESİ) backend zaten performance.goal.notFound (404) döner —
  // gereksiz bir başarısız çağrı atmamak için goalCount>0 da aranır (GoalEditor'ün
  // canApproveClosure deseniyle aynı önleyici yaklaşım).
  const canEqualize = !!selectedPeriod
    && GOAL_WRITING_OPEN_STATUSES.includes(selectedPeriod.status)
    && weightBand.goalCount > 0;

  const [equalizeBusy, setEqualizeBusy] = useState(false);
  const handleEqualize = useCallback(async () => {
    if (!selectedPeriodId) return;
    setEqualizeBusy(true);
    const ok = await equalizeWeights(selectedPeriodId);
    setEqualizeBusy(false);
    if (ok) refresh();
  }, [selectedPeriodId, equalizeWeights, refresh]);

  // #71 — toplu onaya gönderme: çoklu seçim + sonuç (self-servis, tek hedef
  // akışına [openGoal/submitGoal] HİÇ dokunulmadı). Seçim yalnız gönderilebilir
  // (draft|revision_required — EDITABLE_STATUSES ile AYNI küme, backend'in
  // SubmitAsync kapısıyla parite) satırlarda mümkün — kolon aşağıda bu kümenin
  // DIŞINDaki satırlarda checkbox render ETMEZ.
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // DataTable.js'in kendi seçim state'ini (bkz. o dosyanın "Reset selection
  // when data changes" useEffect'i) BİREBİR taşıyan desen — sayfa/filtre/
  // yenileme değiştiğinde stale bir seçim sızıntı yapmasın.
  useEffect(() => { setSelectedIds([]); }, [goals]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }, []);

  const handleBulkSubmit = useCallback(async () => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    setBulkResult(null);
    const res = await submitBulkGoals(selectedIds);
    setBulkBusy(false);
    if (res) {
      // Başlıklar YENİLEMEDEN ÖNCEKİ `goals` kapanışından çözülür — refresh()
      // sonrası liste değişebileceğinden (sayfalama/durum değişimi) sonuç
      // panelindeki adlar bu anlık görüntüye sabitlenir.
      const failedWithTitles = (res.failed || []).map((item) => ({
        ...item,
        title: goals.find((g) => g.id === item.goalId)?.title || item.goalId,
      }));
      setBulkResult({ submittedCount: (res.submitted || []).length, failed: failedWithTitles });
      setSelectedIds([]);
      refresh();
    }
  }, [selectedIds, submitBulkGoals, refresh, goals]);

  const periodOptions = useMemo(() => periods.map((p) => ({
    id: p.id,
    name: `${p.name} (${t(`performance.period.status.${p.status}`, { defaultValue: p.status })})`,
  })), [periods, t]);

  const apiHooks = useMemo(() => ({
    createDraft, updateDraft, submitGoal, updateKrProgress, updateKpiActual,
    submitFinalKpi, requestRevision, setParent, setAlignedOrgGoal, submitEmployeeScores, approveClosure,
  }), [createDraft, updateDraft, submitGoal, updateKrProgress, updateKpiActual,
    submitFinalKpi, requestRevision, setParent, setAlignedOrgGoal, submitEmployeeScores, approveClosure]);

  const openNewGoal = useCallback(() => {
    setActiveGoal(null);
    setEditorOpen(true);
  }, []);

  const openGoal = useCallback(async (row) => {
    const detail = await getById(row.id);
    setActiveGoal(detail || row);
    setEditorOpen(true);
  }, [getById]);

  // Gömülü kullanım — Özet'teki hedef kartından gelinen hedef, dönem seçildikten sonra bir kez açılır.
  const openedGoalRef = useRef(null);
  useEffect(() => {
    if (!openGoalId || !selectedPeriodId || openedGoalRef.current === openGoalId) return;
    openedGoalRef.current = openGoalId;
    getById(openGoalId).then((detail) => {
      if (detail) {
        setActiveGoal(detail);
        setEditorOpen(true);
      }
    });
  }, [openGoalId, selectedPeriodId, getById]);

  const handleSaved = useCallback(() => {
    setEditorOpen(false);
    setActiveGoal(null);
    refresh();
  }, [refresh]);

  // Müşteri geri bildirimi (2026-10-28) — "Dönem kapanmış ve onayda bekleyen bir hedef için
  // hiçbir işlem yapılamıyor… Silinmesine de izin verilmiyor." ⋮ menüsünde "Sil" yalnız
  // silinebilir durumlarda (isDeletableGoalStatus — backend GoalLifecycleRules ile aynı küme);
  // dönem durumu sorulmaz. Onay ConfirmDialog'la; başarı/ret toast'unu interceptor basar.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    const ok = await deleteGoal(deleteTarget.id);
    setDeleteBusy(false);
    setDeleteTarget(null);
    if (ok) refresh();
  }, [deleteTarget, deleteGoal, refresh]);

  const handleDataStateChange = useCallback((_action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  // Hedef girişi yalnız AÇIK (active) dönemde — dönem "evaluation" fazındaysa
  // yeni hedef eklenemez, yalnız mevcutlar puanlanır (müşteri geri bildirimi
  // deseni, bkz. eski MyGoalsPage commit 679367b).
  const canCreateGoal = selectedPeriod?.status === 'active';

  const columns = useMemo(() => [
    {
      name: 'bulkSelect',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = goals[tableMeta.rowIndex];
          if (!row || !EDITABLE_STATUSES.includes(row.status)) return null;
          return (
            // stopPropagation: hücre tıklaması satırın onRowClick'ini (GoalEditor
            // açan) TETİKLEMEMELİ — tek hedef akışı buradan HİÇ etkilenmez.
            <BulkSelectCell data-role={`bulk-select-${row.id}`} onClick={(e) => e.stopPropagation()}>
              <CheckboxInput checked={selectedIds.includes(row.id)} onChange={() => toggleSelect(row.id)} />
            </BulkSelectCell>
          );
        },
      },
    },
    ...baseColumns,
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = goals[tableMeta.rowIndex];
          if (!row) return null;
          const actions = [{
            type: 'edit',
            label: t('performance.common.edit'),
            onClick: () => openGoal(row),
          }];
          if (isDeletableGoalStatus(row.status)) {
            actions.push({
              type: 'delete',
              label: t('performance.common.delete'),
              onClick: () => setDeleteTarget(row),
            });
          }
          return <OptionsMenu actions={actions} row={row} />;
        },
      },
    },
  ], [baseColumns, goals, t, openGoal, selectedIds, toggleSelect]);

  // Koşullu render'ın İÇİNDE değil, üst seviyede çağrılıyor (Hooks kuralları —
  // her render'da aynı sırada çağrılmalı; JSX'te yalnız sonucu koşullu gösteriliyor).
  // Müşteri geri bildirimi (2026-10-28) — satıra tıklamak hedefi açar (onRowClick → openGoal:
  // GET my/{id} tekil okuması "kimde onayda bekliyor" bilgisini taşır). Seçim kutusu hücresi
  // ve ⋮ menüsü kendi tıklamalarında stopPropagation yapar — satır tıklaması tetiklenmez.
  const dataTable = useMemo(() => (
    <DataTable
      title={t('performance.goal.title')}
      fetching={goalsFetching}
      columns={columns}
      data={goals}
      totalRowCount={pagination.totalRowCount}
      pageIndex={pagination.pageIndex}
      pageSize={pagination.pageSize}
      canCreateNewRecord={canCreateGoal}
      newButtonLabel={t('performance.goal.new')}
      onNewButtonClick={openNewGoal}
      onRowClick={openGoal}
      filterList={filterList}
      onFilterConfirm={handleFilterConfirm}
      onTableChanged={handleDataStateChange}
      tableBodyMaxHeight={tableBodyMaxHeight}
    />
  ), [
    t, goalsFetching, columns, goals, pagination, canCreateGoal,
    openNewGoal, openGoal, filterList, handleFilterConfirm, handleDataStateChange, tableBodyMaxHeight,
  ]);

  return (
    <Container>
      {editorOpen && (
        <GoalEditor
          open={editorOpen}
          onClose={() => { setEditorOpen(false); setActiveGoal(null); }}
          goal={activeGoal}
          periodId={selectedPeriodId}
          periodStatus={selectedPeriod?.status}
          periodUsesGoalWeights={selectedPeriod?.usesGoalWeights}
          periodStartDate={selectedPeriod?.startDate}
          periodEndDate={selectedPeriod?.endDate}
          settings={settings}
          apiHooks={apiHooks}
          onSaved={handleSaved}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title={t('performance.goal.deleteDialog.title')}
        message={t('performance.goal.deleteDialog.message', { name: deleteTarget?.title || '' })}
        confirmText={t('performance.common.delete')}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteBusy}
      />

      <PeriodSelectWrap>
        <GenericSelect
          label={t('performance.goal.selectPeriod')}
          value={selectedPeriodId}
          onChange={setSelectedPeriodId}
          options={periodOptions}
          idKey="id"
          labelKey="name"
        />
      </PeriodSelectWrap>

      {!periodsFetching && periods.length === 0 && (
        <Card><EmptyBox>{t('performance.goal.noPeriods')}</EmptyBox></Card>
      )}

      {periods.length > 0 && !selectedPeriodId && (
        <Card><EmptyBox>{t('performance.goal.selectPeriodFirst')}</EmptyBox></Card>
      )}

      {selectedPeriodId && (
        <>
          <HintRow>
            <HintText>
              {selectedPeriod && `${formatDate(selectedPeriod.startDate, lang)} — ${formatDate(selectedPeriod.endDate, lang)}`}
            </HintText>
            {!canCreateGoal && <HintText>{t('performance.goal.periodNotActiveHint')}</HintText>}
          </HintRow>

          {/* Plan 3, Task 8 — kalıcı ağırlık toplamı bandı, yalnız dönem
              usesGoalWeights=true iken (parite: bayraksız dönemde HİÇ render
              edilmez — GoalEditor'ın ağırlık alanlarıyla AYNI koşul). */}
          {selectedPeriod?.usesGoalWeights && (
            <WeightBand data-role="weight-band" $valid={weightBand.isValid}>
              <span data-role="weight-band-summary">
                {t('performance.goal.weightBand.label')}: {weightBand.sum}/100 {weightBand.isValid ? '✓' : '⚠'}
              </span>
              {weightBand.hasMissing && (
                <MissingTag data-role="weight-band-missing">
                  {t('performance.goal.weightBand.missingHint')}
                </MissingTag>
              )}
              {weightGateError && (
                <GateFlash data-role="weight-gate-flash">
                  {t('performance.goal.weightBand.gateError')}: {weightGateError.currentSum}/100
                </GateFlash>
              )}
              <Button
                data-role="equalize-weights-btn"
                variant="outline"
                onClick={handleEqualize}
                disabled={!canEqualize || equalizeBusy}
                loading={equalizeBusy}
              >
                {t('performance.goal.weightBand.equalizeButton')}
              </Button>
            </WeightBand>
          )}

          {/* #71 — çoklu seçim çubuğu: yalnız ≥1 gönderilebilir satır seçiliyken. */}
          {selectedIds.length > 0 && (
            <BulkBar data-role="bulk-submit-bar">
              {/* Sayı, WeightBand.label ile AYNI desen — düz JS birleştirme (i18next
                  {{count}} interpolasyonuna DEĞİL, weightBand.sum/100 ile AYNI
                  yönteme güvenilir; test kimlik mock'unda da doğrulanabilir). */}
              <BulkCount data-role="bulk-selected-count">
                {t('performance.goal.bulk.selectedCountLabel')}: {selectedIds.length}
              </BulkCount>
              {/* #72 — yerel BulkSubmitCta kaldırıldı, paylaşılan Button'a
                  bağlandı (T2/T4'ün "PrimaryCta" tarifi artık Button.js'in
                  varsayılan "primary" varyantı — bkz. t6-report.md). */}
              <Button
                type="button"
                data-role="bulk-submit-btn"
                onClick={handleBulkSubmit}
                disabled={bulkBusy}
              >
                {bulkBusy ? t('common.saving') : t('performance.goal.bulk.submitSelected')}
              </Button>
            </BulkBar>
          )}

          {bulkResult && (
            <ResultBox data-role="bulk-result">
              <ResultHeader>
                <ResultSummary data-role="bulk-result-summary">
                  {t('performance.goal.bulk.resultSummaryLabel')}: {bulkResult.submittedCount}
                </ResultSummary>
                <DismissBtn type="button" aria-label={t('common.close')} onClick={() => setBulkResult(null)}>×</DismissBtn>
              </ResultHeader>
              {bulkResult.failed.length > 0 && (
                <>
                  <FailedTitle>{t('performance.goal.bulk.resultFailedTitle')}</FailedTitle>
                  {bulkResult.failed.map((item) => (
                    <FailedRow key={item.goalId} data-role="bulk-result-failed-row">
                      <span>{item.title}</span>
                      <span>{t(`responseErrors.${item.reason}`, { defaultValue: item.reason })}</span>
                    </FailedRow>
                  ))}
                </>
              )}
            </ResultBox>
          )}

          {dataTable}
        </>
      )}
    </Container>
  );
}
